import { answerChoicesShown } from './answerChoices';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  BrowserGeolocation,
  HoldGate,
  STING_HOLD_DEADLINE_MS,
  cssVarsForTheme,
  flagEnabled,
  isVoiceChip,
  mountExchangeLogOverlay,
  phaseReleasesHold,
  resolveExperienceTheme,
  themeKeyFromSearch,
  toHudView,
} from '@aeonia/gaia-client';
import type { ExperienceTheme, HudView, SessionLine } from '@aeonia/gaia-client';
import { DossierCard } from './card/DossierCard';
import { requestCompassPermission } from './map/useHeading';
import { headerWord } from './headerWord';
import ChipRow from './ChipRow';
import NavigationOverlay from './nav/NavigationOverlay';
import Sting from './Sting';
import type { StingHandle } from './Sting';
import useBeatQueue from './useBeatQueue';
import useDirectiveCues from './useDirectiveCues';
import useDpad from './useDpad';
import useSession from './useSession';
import useVoiceTurn from './useVoiceTurn';
import { isSpokenLineReveal } from './voiceReveal';

declare global {
  interface Window {
    __view?: HudView;
  }
}

function queryFlags(): { sting: boolean; sting2d: boolean; stingBreakShader: boolean } {
  if (typeof location === 'undefined') {
    return { sting: false, sting2d: false, stingBreakShader: false };
  }
  const search = location.search;
  const sting2d = flagEnabled(search, 'sting2d');
  // Dev-only: a test-only way to make the shader fail to compile, so the
  // shader-compile-failure fallback path can be exercised in e2e tests.
  const stingBreakShader = import.meta.env.DEV && flagEnabled(search, 'stingBreakShader');
  return { sting: sting2d || flagEnabled(search, 'sting'), sting2d, stingBreakShader };
}

function readThemeKey(): string {
  if (typeof location === 'undefined') return 'mu-agency';
  return themeKeyFromSearch(location.search);
}

/**
 * Ruling, Jason, 2026-09-28: "o¿o is Throckmorton" — the mark is his
 * identity, so this card never prints a speaker's name. `status` is only
 * ever the current step's authored display name (stepTitle), never a
 * speaker, and is omitted (mark alone) when no step title is known.
 */
function TextBlock({ line, reveal, playbackStarted, playbackDurationMs, playbackComplete }: {
  theme: ExperienceTheme;
  line: SessionLine | null;
  reveal: boolean;
  playbackStarted?: boolean;
  playbackDurationMs?: number | null;
  playbackComplete?: boolean;
}) {
  return <DossierCard
    status={headerWord(line?.stepTitle, line?.text)}
    body={line?.text ?? 'Standing by.'}
    reveal={reveal}
    playbackStarted={playbackStarted}
    playbackDurationMs={playbackDurationMs}
    playbackComplete={playbackComplete}
  />;
}

/**
 * The amber mark for a system state (spec section 6: offline, stopped,
 * warning). A filled triangle with a knocked-out exclamation bar, never an
 * outline, since a 3px-floor outline at this size would vanish. These states
 * are the system talking, never Throckmorton, so this is a different mark
 * from his and only ever appears beside system wording.
 */
function AmberMark() {
  return (
    <svg className="glasses-status-mark" width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 2 L22 20 H2 Z" fill="#FFC14D" />
      <rect x="10.5" y="8" width="3" height="7" fill="#000" />
      <rect x="10.5" y="17" width="3" height="2.5" fill="#000" />
    </svg>
  );
}

/**
 * The connecting/reconnecting loader (spec section 6): a label only for the
 * first 2s, then a ring that draws a single revolution over 1.2s and holds
 * full. It never spins — one iteration, held at the end — matching the
 * display's no-looping-motion rule. Reduced motion is handled in CSS
 * (glasses-status-ring skips the draw-in and shows the held ring at once).
 */
function LoaderRing() {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const timer = window.setTimeout(() => setShow(true), 2000);
    return () => window.clearTimeout(timer);
  }, []);
  if (!show) return null;
  return (
    <svg className="glasses-status-ring" width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
      <circle cx="8" cy="8" r="6" fill="none" stroke="currentColor" strokeWidth="3" />
    </svg>
  );
}

export default function GlassesApp({ phone = false }: { phone?: boolean }) {
  const theme = resolveExperienceTheme(readThemeKey());
  const metaSize = `${theme.glasses.typePx.meta}px`;
  const statusSize = `${theme.glasses.typePx.small}px`;
  const buildStamp = import.meta.env.VITE_BUILD_STAMP ?? 'dev';

  const { state, start, sendTalk, retry, canStart, session } = useSession();
  // The first spoken line is fetched while the opening animation hides the card,
  // but played only once the card is on screen: this gate is released with the cards
  // (or by the same deadline that releases the animation) and stays open after.
  const [openingGate] = useState(() => new HoldGate());
  const { voice, press, enableSpeech } = useVoiceTurn(session, openingGate);

  const flags = useRef(queryFlags()).current;
  // The sting plays alone: while it runs the cards are not on screen at all.
  // On glasses the intro screen waits for Enter; a phone has no Enter key, so
  // it shows Start first and the tap that starts the session plays the intro.
  const [showSting, setShowSting] = useState(flags.sting && !phone);
  // Before the first sting there is nothing on screen but the status line.
  const [cardsReady, setCardsReady] = useState(false);
  // Beats only start their hold once the card is actually on screen to show them.
  const beat = useBeatQueue(session, voice, cardsReady);
  // The presentation-directive player runs alongside the beat, on the same
  // gate (cardsReady): one cue at a time in the card slot, per doc 10's
  // screen-ownership rule (directives own only the card slot, never the
  // answer row's mic/type buttons).
  const {cue, navigation, choice} = useDirectiveCues(session, cardsReady, beat?.line ?? null);
  const view = toHudView(state, voice, beat);
  const revealSpokenLine = isSpokenLineReveal(voice, view.line?.text);
  // display.choice: one or two options, each on its own full-width row above
  // the answer buttons (Jason, 2026-09-28, option B), shown only when
  // source.response says a spoken or typed answer is accepted. Read off the
  // current line, since it never owns the card slot.
  const choiceOptions = answerChoicesShown(location.search)
    && state.authoritativeResponse?.awaiting
    && (!choice.stepId || choice.stepId === state.mission?.stepId)
    && state.authoritativeResponse.accepts.some((kind) => ['voice_transcript', 'text', 'typed_text'].includes(kind))
      ? choice.options : [];
  const chipLabels = view.chipLabels;
  const stingRef = useRef<StingHandle>(null);
  // Set on the first Enter and cleared the moment a phase releases the hold,
  // so the hold can never park past the deadline even if the session never
  // settles into one of the releasing phases below.
  const holdDeadlineTimer = useRef(0);
  // Asked once on the same tap that starts the sting (Jason, 2026-09-29:
  // "if it's a question of getting device permission for location, that can
  // happen early"). Only the outcome is kept; no tracking starts and no
  // position is sent here, only when a walk actually arrives.
  const [locationPermission, setLocationPermission] = useState<'granted' | 'denied' | 'unavailable' | null>(null);
  const locationPermissionRequested = useRef(false);

  useDpad();

  const startSting = useCallback(() => {
    setShowSting(true);
  }, []);

  // The component has to be on screen before it can be told to play, so the
  // play call waits for the render that mounts it.
  useEffect(() => {
    if (showSting) stingRef.current?.play();
  }, [showSting]);

  // The sting parks at its hold until the session has settled one way or the
  // other, so the ring lands on a page that has something to show. If the
  // session never settles, the deadline below releases it anyway so the hold
  // can never park forever.
  useEffect(() => {
    if (phaseReleasesHold(state.phase)) {
      window.clearTimeout(holdDeadlineTimer.current);
      holdDeadlineTimer.current = 0;
      stingRef.current?.release();
    }
  }, [state.phase]);

  useEffect(() => {
    return () => window.clearTimeout(holdDeadlineTimer.current);
  }, []);

  // Desktop testing aid: L toggles a panel showing the exchange log over the
  // display. The glasses have no L key, so this never affects device input.
  useEffect(() => {
    return mountExchangeLogOverlay(session);
  }, [session]);

  // Both the phone tap and glasses Enter keep audio and permission requests
  // within the original user gesture. The live session phase guards double taps.
  const begin = useCallback(() => {
    if (!canStart()) return;
    startSting();
    start();
    enableSpeech();
    if (phone) void requestCompassPermission();
    if (!locationPermissionRequested.current && !(import.meta.env.DEV && new URLSearchParams(location.search).get('demoLocation') === '1')) {
      locationPermissionRequested.current = true;
      void new BrowserGeolocation().requestPermission().then((permission) => {
        setLocationPermission(permission === 'granted' ? 'granted' : permission === 'unsupported' ? 'unavailable' : 'denied');
      });
    }
    holdDeadlineTimer.current = window.setTimeout(() => {
      holdDeadlineTimer.current = 0;
      stingRef.current?.release();
      openingGate.release();
    }, STING_HOLD_DEADLINE_MS);
  }, [startSting, start, canStart, enableSpeech, openingGate, phone]);

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent): void {
      if (event.key !== 'Enter') return;
      const active = document.activeElement;
      if (active instanceof HTMLElement && active.classList.contains('focusable')) return;
      if (active instanceof HTMLInputElement) return;
      begin();
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [begin]);

  useEffect(() => {
    if (cardsReady) openingGate.release();
  }, [cardsReady, openingGate]);

  // Test hooks: let a browser test drive the sting without synthesising keys,
  // and read the current screenful without scraping the page.
  useEffect(() => {
    if (!import.meta.env.DEV) return;
    window.__sting = {
      play: () => startSting(),
      release: () => stingRef.current?.release(),
    };
    return () => {
      delete window.__sting;
    };
  }, [startSting]);

  useEffect(() => {
    if (!import.meta.env.DEV) return;
    window.__view = view;
  });

  function onChipSelect(label: string): void {
    if (isVoiceChip(label)) {
      press();
      return;
    }
    if (state.phase === 'stopped') {
      retry();
      return;
    }
    sendTalk(label);
  }

  return (
    <div className="glasses-root">
      <style>{cssVarsForTheme(theme)}</style>

      {showSting ? (
        <Sting
          ref={stingRef}
          force2d={flags.sting2d}
          breakShader={flags.stingBreakShader}
          onDone={() => {
            setShowSting(false);
            setCardsReady(true);
          }}
        />
      ) : (
        <>
          <div className="glasses-bracket glasses-bracket-top-left" />
          <div className="glasses-bracket glasses-bracket-top-right" />
          <div className="glasses-bracket glasses-bracket-bottom-left" />
          <div className="glasses-bracket glasses-bracket-bottom-right" />

          {/* Status and build stamp are developer chrome; production shows status only when it matters.
              Amber mark for offline/stopped (spec section 6: these are the system talking, never
              Throckmorton, so no mark like his); the one-shot loader ring for connecting/reconnecting. */}
          {(import.meta.env.DEV || view.status !== 'connected') && (
            <div className="glasses-status" style={{ fontSize: statusSize }}>
              {(state.phase === 'offline' || state.phase === 'stopped') && <AmberMark />}
              <span>{phone && state.phase === 'idle' ? 'Ready' : view.status}</span>
              {(state.phase === 'connecting' || state.phase === 'reconnecting') && <LoaderRing />}
            </div>
          )}
          {import.meta.env.DEV && (
            <div className="glasses-build-stamp" style={{ fontSize: metaSize }}>
              {buildStamp}
              {import.meta.env.DEV && new URLSearchParams(location.search).get('demoLocation') === '1' && (
                <span data-demo-device={localStorage.getItem('aeonia:device-id') ?? ''}> · Simulated movement · Device {localStorage.getItem('aeonia:device-id')}</span>
              )}
            </div>
          )}

          {phone && state.phase === 'idle' && !cardsReady && (
            <div className="phone-start"><button className="focusable glasses-chip" onClick={begin}>Start</button></div>
          )}
          {cardsReady ? (
            <div className="glasses-content">
              {/* R-DISP-03/04: one cue at a time, then clear. When the presentation
                  queue has nothing queued, the card area is empty black, not a
                  placeholder card. Navigation, when active, takes this same slot
                  (R-DISP-06: the Go verb's directional cue). */}
              {navigation?.target ? (
                <NavigationOverlay
                  key={`${navigation.sessionId}:${navigation.stepId}:${navigation.target.lat}:${navigation.target.lng}`}
                  session={session}
                  deviceHeading={phone}
                  target={navigation.target}
                  label={navigation.target.label ?? 'Destination'}
                  arrivalRadiusM={navigation.target.arrivalRadiusM}
                  show={navigation.show}
                  search={typeof location === 'undefined' ? '' : location.search}
                  revealKey={0}
                  locationPermission={import.meta.env.DEV && new URLSearchParams(location.search).get('demoLocation') === '1' ? 'granted' : locationPermission}
                  lineText={view.line?.text}
                  lineCard={view.line ? (
                    <TextBlock
                      theme={theme}
                      line={view.line}
                      reveal={revealSpokenLine}
                      playbackStarted={revealSpokenLine && voice.playbackStarted}
                      playbackDurationMs={revealSpokenLine ? voice.playbackDurationMs : null}
                      playbackComplete={revealSpokenLine && voice.playbackComplete}
                    />
                  ) : null}
                />
              ) : cue && cue.method === 'display.text' ? (
                <DossierCard variant="status" body={String(cue.params.text ?? '')} />
              ) : cue && cue.method === 'display.target' ? (
                <DossierCard variant="status" body={String(cue.params.label ?? '')} />
              ) : view.line ? (
                <TextBlock
                  theme={theme}
                  line={view.line}
                  reveal={revealSpokenLine}
                  playbackStarted={revealSpokenLine && voice.playbackStarted}
                  playbackDurationMs={revealSpokenLine ? voice.playbackDurationMs : null}
                  playbackComplete={revealSpokenLine && voice.playbackComplete}
                />
              ) : null}
              <ChipRow
                key={[...choiceOptions, ...chipLabels].join('|')}
                labels={chipLabels}
                choices={choiceOptions}
                theme={theme}
                disabled={!view.chipsEnabled}
                micChipEnabled={view.micChipEnabled}
                onSelect={onChipSelect}
                onSendText={sendTalk}
              />
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}
